import i18next from "i18next";
import { addMonths, differenceInDays, startOfDay } from "date-fns";
import { Theme } from "@mui/material";
import { ContractDto, ContractProviderRef } from "@rentablo/api/contracts";
import { ContractSubCategory, ContractCategory, ProgressData } from "./types";

export const getSubCategoryColors = (theme: Theme) => ({
  background: theme.palette.background.neutral,
  text: theme.palette.text.primary,
});

export const getContractCategoryColors = (
  theme: Theme
): {
  [key in ContractCategory]: {
    background: string;
    text: string;
  };
} => {
  return {
    7: {
      background: theme.palette.graphVisual["01"].subtle,
      text: theme.palette.graphVisual["01"].dark,
    },
    8: {
      background: theme.palette.graphVisual["03"].subtle,
      text: theme.palette.graphVisual["03"].dark,
    },
    1: {
      background: theme.palette.graphVisual["12"].subtle,
      text: theme.palette.graphVisual["12"].dark,
    },
    6: {
      background: theme.palette.graphVisual["11"].subtle,
      text: theme.palette.graphVisual["11"].dark,
    },
    4: {
      background: theme.palette.graphVisual["10"].subtle,
      text: theme.palette.graphVisual["10"].dark,
    },
    2: {
      background: theme.palette.graphVisual["06"].subtle,
      text: theme.palette.graphVisual["06"].dark,
    },
    3: {
      background: theme.palette.graphVisual["07"].subtle,
      text: theme.palette.graphVisual["07"].dark,
    },
    5: {
      background: theme.palette.graphVisual["09"].subtle,
      text: theme.palette.graphVisual["09"].dark,
    },
    74: {
      background: theme.palette.graphVisual["02"].subtle,
      text: theme.palette.graphVisual["02"].dark,
    },
    75: {
      background: theme.palette.graphVisual["05"].subtle,
      text: theme.palette.graphVisual["05"].dark,
    },
    notClassified: {
      background: theme.palette.graphVisual["08"].subtle,
      text: theme.palette.graphVisual["08"].dark,
    },
  };
};

export const toCategoryId = (
  subCategory: ContractSubCategory
): number | undefined =>
  typeof subCategory === "number" ? subCategory : undefined;

export const getContractCategories = (contract?: ContractDto) => {
  if (!contract)
    return {
      category: "notClassified" as ContractCategory,
      subCategory: "notClassified" as ContractSubCategory,
    };

  const categoryObj = contract.subCategory
    ? contract.subCategory
    : contract.autoAssignedSubCategory;
  return {
    category:
      (categoryObj?.parentCategory?.id as ContractCategory) ?? "notClassified",
    subCategory: (categoryObj?.id ?? "notClassified") as ContractSubCategory,
  };
};

export const getContractProgressData = (
  contract: ContractDto
): ProgressData => {
  if (
    (contract.paymentIntervalInMonths ?? 0) <= 3 ||
    !contract.latestCancellationDate ||
    !contract.lastPaymentDate
  ) {
    return {
      percentage: 100,
      color: "inactive",
      expiresInDays: 0,
    };
  }

  const startDate = startOfDay(new Date(contract.lastPaymentDate));
  const endDate = startOfDay(new Date(contract.latestCancellationDate));
  const currentDate = startOfDay(new Date());

  const durationDays = differenceInDays(endDate, startDate);
  const daysRemaining = differenceInDays(endDate, currentDate);
  if (durationDays <= 0) {
    return {
      percentage: 100,
      color: "inactive",
      expiresInDays: 0,
    };
  }
  const percentageRemaining = (daysRemaining / durationDays) * 100;

  const warningPeriod = 14;
  const errorPeriod = 7;
  const progressColor =
    daysRemaining < errorPeriod
      ? "error"
      : daysRemaining <= warningPeriod
        ? "warning"
        : "success";

  return {
    percentage: percentageRemaining < 1 ? 1 : Math.trunc(percentageRemaining),
    color: progressColor,
    expiresInDays: daysRemaining < 1 ? 1 : daysRemaining,
  };
};

export const getNextPaymentInDays = (contract: ContractDto) => {
  if (!contract.lastPaymentDate || contract.paymentIntervalInMonths == null)
    return 0;

  const nextDate = addMonths(
    startOfDay(new Date(contract.lastPaymentDate)),
    contract.paymentIntervalInMonths
  );

  return differenceInDays(nextDate, startOfDay(new Date()));
};

export const formatContractName = (
  subCategoryId?: ContractSubCategory,
  provider?: string
) => {
  if (!subCategoryId && !provider) return "-";

  return `${provider ?? ""}${provider && subCategoryId ? " - " : ""}${
    subCategoryId
      ? i18next.t(`contractAnalyzer.subCategories.${subCategoryId}`)
      : ""
  }`;
};

export const getContractName = (contract?: ContractDto) => {
  if (!contract) return "";
  const { subCategory } = getContractCategories(contract);
  const providerName = contract.userDefinedProvider
    ? contract.userDefinedProvider
    : (contract.autoAssignedProvider?.name ?? "");
  return formatContractName(subCategory, providerName);
};

export const getContractProvider = (
  contract?: ContractDto
): ContractProviderRef | undefined => {
  if (!contract || contract.userDefinedProvider) return undefined;
  return contract.autoAssignedProvider ?? undefined;
};
